import { spawn, execSync, type ChildProcess } from 'node:child_process'
import type { Socket } from 'node:net'
import { writeFileSync, unlinkSync, readdirSync } from 'node:fs'
import { join } from 'node:path'
import { tmpdir } from 'node:os'
import { logger } from './index.js'

export interface TunnelConfig {
  server: string
  serverPort: number
  token?: string
  subdomain: string
  localPort: number
  /** Public host for the tunnel URL. Defaults to server if not set. */
  publicHost?: string
}

let tunnelProcess: ChildProcess | null = null
let tmpConfigPath: string | null = null
let tmpInitialPath: string | null = null
let outputBuffer = ''

export function buildFrpcConfig(config: TunnelConfig): string {
  const lines = [
    `serverAddr = "${config.server}"`,
    `serverPort = ${config.serverPort}`,
  ]

  if (config.token) {
    lines.push(`auth.token = "${config.token}"`)
  }

  lines.push(
    '',
    '[[proxies]]',
    `name = "${config.subdomain}"`,
    'type = "http"',
    `localPort = ${config.localPort}`,
    `subdomain = "${config.subdomain}"`,
  )

  return lines.join('\n') + '\n'
}

export function getTunnelUrl(subdomain: string, publicHost: string): string {
  return `https://${subdomain}.${publicHost}`
}

function isFrpcInstalled(): boolean {
  try {
    execSync('command -v frpc', { stdio: 'ignore' })
    return true
  } catch {
    return false
  }
}

function configPrefix(subdomain: string): string {
  return `frpc-${subdomain}-`
}

/**
 * Kill orphaned frpc processes from previous runs of the same subdomain.
 * Identifies them by the `frpc-{subdomain}-{frpcPid}.toml` config path pattern.
 * The PID in the filename is the frpc child PID (not the parent node PID),
 * so when node dies and frpc survives as orphan (we unref it), we can still
 * find and terminate it — otherwise the FRPS keeps the subdomain reserved
 * and new frpc spawns fail silently.
 */
function killOrphanedFrpcProcesses(subdomain: string): void {
  // Scan BOTH /tmp and os.tmpdir() — on macOS tmpdir() resolves to
  // /var/folders/.../T/ under some contexts but /tmp under others, so
  // orphans from a previous run may live in a different tmpdir than ours.
  const candidateDirs = Array.from(new Set([tmpdir(), '/tmp']))
  const prefix = configPrefix(subdomain)

  for (const dir of candidateDirs) {
    try {
      const staleConfigs = readdirSync(dir).filter(f =>
        f.startsWith(prefix) && f.endsWith('.toml'),
      )

      for (const configFile of staleConfigs) {
        const pidMatch = configFile.match(/(\d+)\.toml$/)
        if (!pidMatch) continue

        const pid = Number(pidMatch[1])
        if (pid === process.pid) continue

        try {
          process.kill(pid, 0) // check if alive
          process.kill(pid, 'SIGTERM')
          logger.info({ pid, component: 'tunnel' }, 'Killed orphaned frpc process')
          // SIGKILL fallback if SIGTERM doesn't work
          const t = setTimeout(() => { try { process.kill(pid, 'SIGKILL') } catch { /* dead */ } }, 800)
          t.unref()
        } catch {
          // Process already gone — just clean up the config file
        }

        try { unlinkSync(join(dir, configFile)) } catch { /* already gone */ }
      }
    } catch {
      // Non-critical — proceed with startup
    }
  }
}

export async function startTunnel(config: TunnelConfig): Promise<boolean> {
  if (tunnelProcess) return true

  if (!isFrpcInstalled()) {
    logger.warn('frpc binary not found — tunnel disabled. Install with: brew install frp')
    return false
  }

  killOrphanedFrpcProcesses(config.subdomain)

  const toml = buildFrpcConfig(config)
  // Initial name uses node pid to avoid collisions when writing; after spawn
  // we rename it to encode the frpc child pid so orphan cleanup can target it.
  const initialPath = join(tmpdir(), `${configPrefix(config.subdomain)}node-${process.pid}.toml`)
  writeFileSync(initialPath, toml)

  tunnelProcess = spawn('frpc', ['-c', initialPath], {
    stdio: ['ignore', 'pipe', 'pipe']
  })

  // Write a copy with the frpc child pid in the filename for orphan cleanup.
  // We CANNOT rename initialPath — frpc may not have opened it yet (race condition).
  const frpcPid = tunnelProcess.pid
  if (frpcPid) {
    const finalPath = join(tmpdir(), `${configPrefix(config.subdomain)}${frpcPid}.toml`)
    try {
      writeFileSync(finalPath, toml)
      tmpConfigPath = finalPath
      tmpInitialPath = initialPath
    } catch {
      tmpConfigPath = initialPath
    }
  } else {
    tmpConfigPath = initialPath
  }

  // Unref so frpc doesn't prevent Node from exiting during shutdown.
  // The event loop can exit even if frpc is still alive.
  tunnelProcess.unref();
  (tunnelProcess.stdout as Socket | null)?.unref?.();
  (tunnelProcess.stderr as Socket | null)?.unref?.()

  outputBuffer = ''
  // Strip ANSI escape codes from frpc output
  // eslint-disable-next-line no-control-regex
  const stripAnsi = (s: string) => s.replace(/\x1b\[[0-9;]*m/g, '')

  return new Promise<boolean>((resolve) => {
    let settled = false

    const collectOutput = (data: Buffer) => {
      const msg = stripAnsi(data.toString()).trim()
      if (!msg) return
      outputBuffer += msg + '\n'
      logger.debug({ component: 'tunnel' }, msg)
      // frpc logs "start proxy success" when connected
      if (!settled && msg.includes('start proxy success')) {
        settled = true
        const url = getTunnelUrl(config.subdomain, config.publicHost ?? config.server)
        logger.info({ url, component: 'tunnel' }, `Tunnel active → ${url}`)
        resolve(true)
      }
    }

    tunnelProcess!.stdout?.on('data', collectOutput)
    tunnelProcess!.stderr?.on('data', collectOutput)

    tunnelProcess!.on('exit', (code) => {
      if (code !== 0 && code !== null) {
        const output = outputBuffer.trim()
        const hint = output.includes('authorization failed')
          || output.includes('auth failed')
          || output.includes('invalid token')
          ? ' — check FRPC_TOKEN is correct'
          : output.includes('login to the server failed')
            ? ` — ${output.split('\n').pop()}`
            : ''
        logger.error(
          { code, reason: output || undefined, component: 'tunnel' },
          `frpc failed to start${hint}`,
        )
      }
      tunnelProcess = null
      outputBuffer = ''
      cleanupConfig()
      if (!settled) {
        settled = true
        resolve(false)
      }
    })

    // Timeout — resolve the promise so callers aren't stuck forever.
    // With non-blocking startup this only affects BACKEND_URL being set.
    const connectTimeout = setTimeout(() => {
      if (!settled) {
        settled = true
        logger.warn({ component: 'tunnel' }, 'frpc connection timeout (15s) — tunnel may still connect in background')
        resolve(false)
      }
    }, 15_000)
    connectTimeout.unref()
  })
}

export function stopTunnel(): void {
  if (!tunnelProcess) {
    cleanupConfig()
    return
  }

  const proc = tunnelProcess
  tunnelProcess = null
  cleanupConfig()

  // SIGTERM lets frpc send "closeProxy" to the server — avoids server-side TIME_WAIT
  // that would block the next startup from connecting to the same subdomain.
  try { proc.kill('SIGTERM') } catch { return }

  // Fallback: SIGKILL if frpc doesn't exit within 800ms
  const fallback = setTimeout(() => {
    try { proc.kill('SIGKILL') } catch { /* already dead */ }
  }, 800)
  fallback.unref()
}

function cleanupConfig(): void {
  if (tmpConfigPath) {
    try { unlinkSync(tmpConfigPath) } catch { /* already gone */ }
    tmpConfigPath = null
  }
  if (tmpInitialPath) {
    try { unlinkSync(tmpInitialPath) } catch { /* already gone */ }
    tmpInitialPath = null
  }
}
